import { Injectable, Inject } from '@angular/core';
import {HttpClient, HttpEvent, HttpRequest} from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { BusinessProcess } from '../Models/businessProcess';
import { DropDownContent } from '../Models/dropDownContent';
import { DesignChangeLogAction } from '../Models/designChangeLogAction';
import { NewConfiguration } from '../Views/Runtime/business-process/history-report-visibility/history-report-visibility.component';
import { RelatedBusinessProcessConfig } from '../Models/relatedBusinessProcessConfig';
import { take } from 'rxjs/operators';
import { ElementLock, ElementLockScreen } from '../Models/elementLock';
import { LockStatus } from '../Models/enum/lockStatus';
import { BusinessProcessVersion } from '../Models/businessProcessVersion';
import { BusinessProcessVersionFilter } from '../Models/businessProcessVersionFilter';
import { DesignElementType } from '../Models/enum/designElementType';
import {HumanInteractionTokenResult} from '../Models/humanInteractionTokenResult';

@Injectable({ providedIn: 'root'})
export class BusinessProcessService {

  private  businessProcesses = new BehaviorSubject<BusinessProcess[]>(null);

  get getPublishedBusinessProcesses() {
    this.getBPList();
    return this.businessProcesses.asObservable();
  }

  constructor(private http: HttpClient,
              @Inject('BASE_URL') private baseUrl: string) { }

  private getBPList() {
    this.http.post<BusinessProcess[]>('/api/bp/BusinessProcessStartList', null).subscribe(result => {
      this.businessProcesses.next(result);
    }, error => console.error(error) );

  }

  openWindowInteraction(instanceId, isPreview: boolean, windowOrigin: number = 8) {
    this.getUrlForHumanInteraction(instanceId, isPreview, windowOrigin)
      .subscribe((resp: any) => {
        if (resp) {
          window.open(resp.url, '_blank');
        }
      });
  }

  openWindowNewInstance(businessProcessId: number) {
    this.getUrlForNewInstance(businessProcessId)
      .subscribe((resp: any) => {
        if (resp) {
          window.open(resp.url, '_blank');
        }
      });
  }

  getTokenForLayoutEditor() {
    return this.http.get('/api/runtime/tokenForLayoutEditor');
  }

  getUrlForHumanInteraction(instanceId, isPreview, windowOrigin) {
    return this.http.post<any>('/api/runtime/urlForHumanInteraction',
      { instanceId: instanceId, isPreview: isPreview, windowOrigin: windowOrigin } );
  }

  getUrlForNewInstance(businessProcessId) {
    return this.http.get<any>(`/api/runtime/urlForNewInstance/${businessProcessId}`);
  }

  getAllBusinessProcesses() {
    return this.http.get<BusinessProcess[]>('api/bp/getAllBusinessProcesses');
  }

  getAllBusinessProcessesPublished() {
    return this.http.get<any[]>('/api/bp/getAllBusinessProcessesPublished');
  }

  getAllBusinessProcessesPublishedWithStates() {
    return this.http.get<any[]>('/api/bp/getAllBusinessProcessesPublishedWithStates');
  }

  getStatesByBusinessProcessName(businessProcessName: string) {
    return this.http.post<DropDownContent[]>('api/bp/getStatesByBusinessProcessName', { businessProcessName: businessProcessName } );
  }

  businessProcessActions(businessProcessId: number, action: string, userChangelog: string = '') {
    // action: informar Delete, Publish ou Consolidate
    return this.http.post<void>('api/bp/businessProcessActions',
      { businessProcessId: businessProcessId, businessProcessAction: action, userChangelog: userChangelog} );
  }

  getBusinessProcessChangeLogEntries(businessProcessId: number) {
    return this.http.post<DesignChangeLogAction[]>('api/bp/businessProcessChangeLogEntries', { businessProcessId: businessProcessId });
  }

  getChangeLogActions(businessProcessId: number, businessProcessVersion: number) {
    return this.http.post<DesignChangeLogAction[]>('api/bp/getChangeLogActions',
      { businessProcessId: businessProcessId, businessProcessVersion: businessProcessVersion });
  }

  getBusinessProcessChangeLog(businessProcessId: number, businessProcessVersion: number) {
    return this.http.post<any>('api/bp/getBusinessProcessChangeLog',
      { businessProcessId: businessProcessId, businessProcessVersion: businessProcessVersion });
  }

  hasBlockItems(businessProcessId: number) {
    return this.http.post<Boolean>('api/bp/hasElementsBlock', { businessProcessId: businessProcessId });
  }

  checkRights(rights) {
    return this.http.post<any[]>('api/rights/checkRights', {requiredRights: [rights]});
  }

  getPublishedFieldsByBusinessProcessId(businessProcessId: number) {
    return this.http.post<any[]>('api/bp/loadFields', { businessProcessId: businessProcessId });
  }

  getBusinessProcessById(businessProcessId: number) {
    return this.http.post<BusinessProcess>('api/bp/getBusinessProcessById', { businessProcessId: businessProcessId });
  }

  getStatesByBusinessProcessSpecificVersion(businessProcessId: number, businessProcessVersion: number) {
    return this.http.post<any[]>('api/bp/loadStates',
      { businessProcessId: businessProcessId, businessProcessVersion: businessProcessVersion });
  }

  getPublishedBusinessProcessById(businessProcessId: number) {
    return this.http.post<BusinessProcess>('api/bp/getPublishedBusinessProcess', { businessProcessId: businessProcessId });
  }

  getBusinessProcessPublicationInfo(businessProcessId: number) {
    return this.http.post<any>('api/bp/getBusinessProcessPublicationInfo', { businessProcessId: businessProcessId });
  }

  getBusinessProcessOldVersions(businessProcessVersionFilter: BusinessProcessVersionFilter) {
    return this.http.post<any>('api/bp/getBusinessProcessOldVersions', businessProcessVersionFilter);
  }

  getHistoryReportViewConfigurations(businessProcessId: number) {
    return this.http.post<any[]>('api/bp/getHistoryReportViewConfigurations', { businessProcessId: businessProcessId });
  }

  createHistoryReportViewConfig(config: NewConfiguration) {
    return this.http.post<void>('api/bp/createHistoryReportViewConfig', config );
  }

  deleteHistoryReportViewConfiguration(businessProcessId: number, groupId: number) {
    return this.http.delete(`api/bp/deleteHistoryReportViewConfiguration/${businessProcessId}/${groupId}`)
      .pipe(take(1));
  }

  loadRelatedBPConfiguration(businessProcessId: number) {
    return this.http.post<RelatedBusinessProcessConfig>('api/bp/loadRelatedBPConfiguration', { businessProcessId: businessProcessId });
  }

  saveRelatedBPConfiguration(businessProcessId: number, relatedBPScopes: number[],
                    metadataStructures: number[], fields: number[], hasConfiguration: boolean) {
    return this.http.post<void>('api/bp/saveRelatedBPConfiguration',
      {
        businessProcessId: businessProcessId,
        relatedBusinessProcessesScopes: relatedBPScopes,
        metadataStructures: metadataStructures,
        fields: fields,
        hasConfiguration: hasConfiguration
      });
  }

  deleteRelatedBPConfiguration(businessProcessId: number) {
    return this.http.delete(`api/bp/deleteRelatedBPConfiguration/${businessProcessId}`).pipe(take(1));
  }

  // elementos bloqueados

  getLightBusinessProcess(businessProcessId: number) {
    return this.http.post<BusinessProcessVersion>('api/bp/getLightBusinessProcessForManagementById',
      { businessProcessId: businessProcessId });
  }

  getBusinessProcessLibrary(businessProcessId: number) {
    return this.http.post<any[]>('api/bp/getBusinessProcessLibraryById', { businessProcessId: businessProcessId });
  }

  getElementLockStatus(businessProcessId: number, lockStatus: LockStatus) {
    return this.http.post<ElementLock[]>('api/bp/getElementLockStatus',
      { businessProcessId: businessProcessId, lockStatus: lockStatus });
  }

  unlockElements(elements: ElementLockScreen[]) {
    console.log('unlockEl', elements);
    return this.http.post<void>('api/bp/unblockElements', { elementLocks: elements });
  }
  // elementos bloqueados - fim

  exportBusinessProcess(businessProcessId: number, businessProcessName: string): Observable<HttpEvent<Blob>> {
    return this.http.request(new HttpRequest(
      'GET',
      `api/bp/export/${businessProcessId}?businessProcessName=${businessProcessName}`,
      null,
      {
        reportProgress: true,
        responseType: 'blob'
      }));
  }

  recoverBusinessProcessVersion(businessProcessId: number, businessProcessVersion: number) {
    return this.http.post<void>('api/bp/recoverBusinessProcessVersion',
      { businessProcessId: businessProcessId, businessProcessVersion: businessProcessVersion});
  }

  /* Edição de processo de negócio */

  getBusinessProcessForManagementById(businessProcessId: number) {
    return this.http.post<any[]>('api/bp/getBusinessProcessForManagementById',
      { businessProcessId: businessProcessId });
  }

  beginEditingElement(elementType: DesignElementType, elementId: number, elementName: string) {
    return this.http.post<void>(
      'api/bp/beginEditingElement', { elementType: elementType, elementId: elementId, elementName: elementName }
    );
  }

  endEditingElement(elementType: DesignElementType, elementId: number, elementName: string) {
    return this.http.post<void>(
      'api/bp/endEditingElement', { elementType: elementType, elementId: elementId, elementName: elementName }
    );
  }

  getCurrentElementLock(elementType: DesignElementType, elementId: number) {
    return this.http.post<ElementLock>('api/bp/getCurrentElementLock',
      { elementType: elementType, elementId: elementId });
  }

  updateBusinessProcess(businessProcessInfo: any) {
    return this.http.post<void>('api/bp/updateBusinessProcess', businessProcessInfo);
  }

  createBusinessProcess(businessProcessInfo: any) {
    return this.http.post<number>('api/bp/createBusinessProcess', businessProcessInfo);
  }

  uploadBusinessProcess(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post('api/bp/uploadBp', formData, {reportProgress: true, observe: 'events'});
  }

  importBusinessProcess(bpName: string) {
    return this.http.post('api/bp/importBp', { businessProcessName: bpName }, {reportProgress: true, observe: 'events'});
  }

  getStateForManagementById(businessProcessId: number, stateId: number) {
    return this.http.get<any[]>(`api/bp/${businessProcessId}/getState/${stateId}`);
  }

  getLibraryByType(businessProcessId: number, libraryItemType: number, libraryItemSubType: number) {
    return this.http.get<any[]>(`api/bp/${businessProcessId}/getLibraryByType/${libraryItemType}/${libraryItemSubType}`);
  }

  getStatesByBusinessProcessId(businessProcessId: number) {
    return this.http.get<any[]>(`api/bp/getStatesByBusinessProcessId/${businessProcessId}`);
  }

  deleteBusinessProcessState(businessProcessId: number, stateId: number) {
    return this.http.delete(`api/bp/deleteBusinessProcessState/${businessProcessId}/${stateId}`);
  }

  updateBusinessProcessState(businessProcessId: number, stateDto: any) {
    return this.http.put(`api/bp/updateBpState/${businessProcessId}`, stateDto);
  }

  createBusinessProcessState(businessProcessId: number, stateDto: any) {
    return this.http.post(`api/bp/createBpState/${businessProcessId}`, stateDto);
  }

  deleteBusinessProcessField(businessProcessId: number, fieldId: number) {
    return this.http.delete(`api/bp/deleteBusinessProcessField/${businessProcessId}/${fieldId}`);
  }

  generateFlowDesignerURL(businessProcessId: number, flowId: number, flowExecutionType: number) {
    const requestData = { businessProcessId: businessProcessId, flowId: flowId, longFlowType: flowExecutionType };
    return this.http.post('api/bp/getFlowDesignerURL', requestData);
  }

  generateLayoutDesignerURL(businessProcessId: number, libraryItemId: number) {
    const requestData = { libraryItemId: libraryItemId,  businessProcessId: businessProcessId };
    return this.http.post('api/bp/getLayoutDesignerURL', requestData);
  }

  createStateLibraryFlow(name: string, businessProcessId: number, itemType: string) {
    return this.http.post<number>('api/bp/createStateLibraryFlow',
      { flowName: name, businessProcessId: businessProcessId, itemType: itemType });
  }

  createStateLibraryLayout(name: string, businessProcessId: number) {
    return this.http.post<number>('api/bp/createStateLibraryLayout',
      { layoutName: name, businessProcessId: businessProcessId });
  }

  createStateLibraryView(name: string, businessProcessId: number) {
    return this.http.post<number>('api/bp/createViewLibraryLayout',
      { layoutName: name, businessProcessId: businessProcessId });
  }

  generateDebuggerURL(businessProcessId: number, stateId: number, flowExecutionType: number) {
    const requestData = { businessProcessId: businessProcessId, stateId: stateId, longFlowType: flowExecutionType };
    return this.http.post('api/bp/getDebuggerURL', requestData);
  }

  getLibraryItems(businessProcessId: number, libraryItemType: number, designElementType: number) {
    return this.http.get<any[]>(`api/bp/getLibraryItems/${businessProcessId}/${libraryItemType}/${designElementType}`);
  }

  updateLibraryItem(businessProcessId: number, designElementType: number, libraryItem: any) {
    return this.http.put(`api/bp/updateBusinessProcessLibraryItem/${businessProcessId}/${designElementType}`, libraryItem);
  }

  deleteLibraryItem(businessProcessId: number, itemLibraryId: number, designElementType: number) {
    return this.http.delete(`api/bp/deleteLibraryItem/${businessProcessId}/${itemLibraryId}/${designElementType}`);
  }

  getReferencingStates(businessProcessId: number, elementId: number, designElementType: number) {
    return this.http.get<any[]>(`api/bp/getReferencingStates/${businessProcessId}/${elementId}/${designElementType}`);
  }

  getUnrelatedLibraryItems(businessProcessId: number, itemType: number, designElementType: number) {
    return this.http.get<any[]>(`api/bp/getUnrelatedLibraryItems/${businessProcessId}/${itemType}/${designElementType}`);
  }

  getFieldsByBusinessProcessId(businessProcessId: number) {
    return this.http.get<any>(`api/bp/getFields/${businessProcessId}`);
  }

  beginEditingGroup(designElementType: number, list) {
    return this.http.post<number>('api/bp/beginEditingGroup',
      { elementType: designElementType, elements: list });
  }

  cancelEditingGroup(designElementType: number, list) {
    return this.http.post<number>('api/bp/cancelEditingGroup',
      { elementType: designElementType, elements: list });
  }

  saveFields(businessProcessId: number, dirtyFields, removedFields) {
    return this.http.post<number>('api/bp/saveFields',
      { businessProcessId: businessProcessId, fields: dirtyFields, fieldsToRemove: removedFields });
  }

  saveField(businessProcessId: number, field) {
    return this.http.post<number>('api/bp/saveField', { businessProcessId: businessProcessId, field: field });
  }

  loadBusinessProcessValidationErrors(businessProcessId: number) {
    return this.http.get<any>(`api/bp/loadBusinessProcessValidationErrors/${businessProcessId}`);
  }

  getGraph(businessProcessId: number) {
    return this.http.get<any>(`api/bp/getGraph/${businessProcessId}`);
  }
}
